import { authHeaders } from '../api.js';
import { codexFailureState, decodeCodexReceipt, type CodexReceipt } from './codex-receipt.js';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';

export type InvestigationPreflight = 'available' | 'unavailable' | 'verification_unavailable' | 'denied';
export type CodexRunFailure = 'unavailable' | 'denied' | 'uncertain' | 'verification_unavailable' | 'in_progress';
export type CodexRunOutcome = { ok: true; receipt: CodexReceipt } | { ok: false; failure: CodexRunFailure };
/** Reconcile also reports that the server holds no unconfirmed start for this chat any more. */
export type CodexReconcileOutcome = CodexRunOutcome | { ok: false; failure: 'not_pending' };
/** Discard additionally reports that the stuck start was abandoned (MC holds no run for its key). */
export type CodexDiscardOutcome = CodexReconcileOutcome | { ok: true; abandoned: true };
/** Consent start: the Mission Control URL to visit, or why none was created. */
export type CodexConsentStart = { url: string } | { error: 'rate_limited' | 'consent_pending' | 'unavailable' };
/** A retained run, or a start whose outcome the server could not confirm. */
export type CodexRunListEntry = { kind: 'run'; receipt: CodexReceipt }
  | { kind: 'unconfirmed' | 'denied' | 'abandoned'; idempotencyKey: string };

/** Matches the server's start schema; a longer request is refused before any network call. */
export const CODEX_MAX_INSTRUCTIONS = 64000;
/** Investigation depth. `deep` needs explicit consent for every run. */
export type CodexTier = 'light' | 'standard' | 'deep';
export type CodexTierOption = { id: CodexTier; label: string; requiresConsent: boolean };
/** Shown when the server's menu cannot be read; mirrors the server's static list. */
export const STATIC_CODEX_TIERS: readonly CodexTierOption[] = [
  { id: 'light', label: 'Light', requiresConsent: false },
  { id: 'standard', label: 'Standard', requiresConsent: false },
  { id: 'deep', label: 'Deep', requiresConsent: true },
];
export const DEFAULT_CODEX_TIER: CodexTier = 'standard';
/** Per-run start options. `deepConsent` must be true for a deep run and is never sent otherwise. */
export type CodexStartOptions = { tier?: CodexTier; deepConsent?: boolean };
/** First-party chatbox grant state for this subscriber in this tenant. */
export type CodexGrantState = 'granted' | 'consent_required' | 'denied' | 'verification_unavailable';
const CONSENT_ORIGIN = 'https://mission-control.lumeniq.ai';

/** A command parser, not an intent classifier. Preserve the instruction body
 * verbatim for the canonical start operation. */
export function investigationInstructions(message: string): string | null {
  const match = /^\/investigate(?:[ \t]|$)/.exec(message);
  return match ? message.slice(match[0].length) : null;
}

function chatPath(sessionId: string, suffix = ''): string {
  return `${API_BASE}/api/subscriber-chat/chats/${encodeURIComponent(sessionId)}${suffix}`;
}

/** Reads only. Reconnect/refresh cannot submit or relaunch an investigation. */
export async function investigationPreflight(sessionId: string): Promise<InvestigationPreflight> {
  try {
    const headers = await authHeaders();
    if (!headers.Authorization) return 'denied';
    const response = await fetch(chatPath(sessionId, '/investigation'), { headers, cache: 'no-store' });
    if ([401, 403, 404].includes(response.status)) return 'denied';
    if (!response.ok) return 'verification_unavailable';
    const body: unknown = await response.json();
    if (typeof body === 'object' && body !== null && 'available' in body) {
      if (body.available === true && Object.keys(body).length === 1) return 'available';
      if (body.available === false && 'code' in body && body.code === 'codex_runtime_unavailable') return 'unavailable';
    }
    // An unfamiliar payload does not establish a supported runtime capability.
    return 'verification_unavailable';
  } catch {
    return 'verification_unavailable';
  }
}

/** Derive the Codex start key from the chat message's own idempotency key, so a
 * retry of the same message reconciles a lost start ACK instead of starting twice. */
export function codexIdempotencyKey(messageKey: string): string {
  return `codex:${messageKey}`;
}

async function call(operation: 'start' | 'status' | 'cancel' | 'reconcile' | 'discard', url: string, init: RequestInit,
  expectedRunId?: string): Promise<CodexDiscardOutcome> {
  let headers: Awaited<ReturnType<typeof authHeaders>>;
  try { headers = await authHeaders(); } catch { return { ok: false, failure: operation === 'status' ? 'verification_unavailable' : 'uncertain' }; }
  // No credential means nothing was sent: a refusal, never an uncertain effect.
  if (!headers.Authorization) return { ok: false, failure: 'denied' };
  let response: Response;
  try {
    response = await fetch(url, { ...init, headers: { ...headers, 'Content-Type': 'application/json' }, cache: 'no-store' });
  } catch {
    // The request may have reached the server: a start/cancel is uncertain, never retried here.
    return { ok: false, failure: operation === 'status' ? 'verification_unavailable' : 'uncertain' };
  }
  let body: unknown = null;
  try { body = await response.json(); } catch { /* treated as unverified below */ }
  if (response.ok) {
    if (operation === 'discard' && body && typeof body === 'object' && !Array.isArray(body)
      && Object.keys(body).length === 1 && (body as Record<string, unknown>).state === 'abandoned') {
      return { ok: true, abandoned: true };
    }
    const receipt = decodeCodexReceipt(body, expectedRunId);
    if (receipt) return { ok: true, receipt };
    return { ok: false, failure: operation === 'status' ? 'verification_unavailable' : 'uncertain' };
  }
  if ((operation === 'reconcile' || operation === 'discard') && response.status === 409 && body && typeof body === 'object' && !Array.isArray(body)
    && (body as Record<string, unknown>).error === 'codex_session_not_pending'
    && (body as Record<string, unknown>).inspect_required === false && (body as Record<string, unknown>).qualified === false) {
    return { ok: false, failure: 'not_pending' };
  }
  // Reconcile and discard act on the same start: their failures carry start semantics.
  return { ok: false, failure: codexFailureState(response.status, body,
    operation === 'reconcile' || operation === 'discard' ? 'start' : operation) };
}

/** Start one owned Codex investigation. The server derives repository, tenant and
 * grant from the owned chat; the client sends only the instructions and key. */
export function startCodexRun(sessionId: string, instructions: string, idempotencyKey: string,
  options: CodexStartOptions = {}): Promise<CodexRunOutcome> {
  // A deep run without explicit consent for this run is refused here, before anything is sent.
  if (options.tier === 'deep' && options.deepConsent !== true) return Promise.resolve({ ok: false, failure: 'denied' });
  const body = { instructions, idempotency_key: idempotencyKey,
    ...(options.tier ? { tier: options.tier } : {}), ...(options.tier === 'deep' ? { deep_consent: true } : {}) };
  // 'not_pending' is produced only for 'reconcile'.
  return call('start', chatPath(sessionId, '/codex-runs'),
    { method: 'POST', body: JSON.stringify(body) }) as Promise<CodexRunOutcome>;
}

/** The server's tier menu; the static list when it cannot be verified. `deep` always requires consent. */
export async function readCodexTiers(): Promise<readonly CodexTierOption[]> {
  try {
    const headers = await authHeaders();
    if (!headers.Authorization) return STATIC_CODEX_TIERS;
    const response = await fetch(`${API_BASE}/api/subscriber-chat/codex-tiers`, { headers, cache: 'no-store' });
    if (!response.ok) return STATIC_CODEX_TIERS;
    const body: unknown = await response.json();
    const list = body && typeof body === 'object' ? (body as { tiers?: unknown }).tiers : undefined;
    if (!Array.isArray(list) || list.length === 0) return STATIC_CODEX_TIERS;
    const out: CodexTierOption[] = [];
    for (const item of list) {
      const row = item && typeof item === 'object' ? item as Record<string, unknown> : {};
      if (row.id !== 'light' && row.id !== 'standard' && row.id !== 'deep') return STATIC_CODEX_TIERS;
      if (out.some((t: CodexTierOption) => t.id === row.id)) return STATIC_CODEX_TIERS;
      out.push({ id: row.id, label: typeof row.label === 'string' && row.label ? row.label : row.id,
        requiresConsent: row.id === 'deep' || row.requires_consent === true });
    }
    return out;
  } catch {
    return STATIC_CODEX_TIERS;
  }
}

/** Explicit user action only ("Check again" with no local copy of the start): asks the server to
 * re-enter its stored unconfirmed start for this chat with the SAME key. Never a new start. */
export function reconcileCodexRun(sessionId: string): Promise<CodexReconcileOutcome> {
  return call('reconcile', chatPath(sessionId, '/codex-runs/reconcile'), { method: 'POST', body: JSON.stringify({}) }) as Promise<CodexReconcileOutcome>;
}

/** Explicit, confirmed user action only ("Discard stuck investigation"): the server asks Mission
 * Control whether a run exists for the stuck start's key. A real run is returned (never discarded);
 * only MC's explicit "no run" abandons the start and frees the chat. Never retried here. */
export function discardCodexRun(sessionId: string): Promise<CodexDiscardOutcome> {
  return call('discard', chatPath(sessionId, '/codex-runs/discard'), { method: 'POST', body: JSON.stringify({}) });
}

export function readCodexRun(sessionId: string, runId: string): Promise<CodexRunOutcome> {
  return call('status', chatPath(sessionId, `/codex-runs/${encodeURIComponent(runId)}`), { method: 'GET' }, runId) as Promise<CodexRunOutcome>;
}

/** Requests a stop. A successful response is cancel_requested until the server
 * reports verified physical cessation. */
export function cancelCodexRun(sessionId: string, runId: string): Promise<CodexRunOutcome> {
  return call('cancel', chatPath(sessionId, `/codex-runs/${encodeURIComponent(runId)}/cancel`),
    { method: 'POST', body: JSON.stringify({}) }, runId) as Promise<CodexRunOutcome>;
}

/** Read only: whether this subscriber holds a live chatbox grant. Never creates one. */
export async function readCodexGrant(): Promise<CodexGrantState> {
  try {
    const headers = await authHeaders();
    if (!headers.Authorization) return 'denied';
    const response = await fetch(`${API_BASE}/api/subscriber-chat/codex-grant`, { headers, cache: 'no-store' });
    if (response.status === 401 || response.status === 403) return 'denied';
    if (!response.ok) return 'verification_unavailable';
    const body: unknown = await response.json();
    if (body && typeof body === 'object' && !Array.isArray(body) && Object.keys(body).length === 1) {
      const granted = (body as { granted?: unknown }).granted;
      if (granted === true) return 'granted';
      if (granted === false) return 'consent_required';
    }
    return 'verification_unavailable';
  } catch {
    return 'verification_unavailable';
  }
}

/** Start one explicit consent. Returns the Mission Control consent URL to navigate to, a
 * rate-limit or already-open refusal, or unavailable when it could not be created or is not a
 * Mission Control URL. Never retried here. */
export async function startCodexConsent(): Promise<CodexConsentStart> {
  const unavailable: CodexConsentStart = { error: 'unavailable' };
  try {
    const headers = await authHeaders();
    if (!headers.Authorization) return unavailable;
    const response = await fetch(`${API_BASE}/api/subscriber-chat/codex-grant`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: '{}', cache: 'no-store' });
    if (response.status === 429) return { error: 'rate_limited' };
    if (response.status === 409) {
      const refusal: unknown = await response.json().catch(() => null);
      return refusal && typeof refusal === 'object' && (refusal as { error?: unknown }).error === 'codex_session_consent_pending'
        ? { error: 'consent_pending' } : unavailable;
    }
    if (response.status !== 201) return unavailable;
    const body: unknown = await response.json();
    const url = body && typeof body === 'object' ? (body as { consent_url?: unknown }).consent_url : undefined;
    if (typeof url !== 'string') return unavailable;
    const parsed = new URL(url);
    // Exact origin, https, and no userinfo (https://x@mission-control... is refused, not stripped).
    return parsed.origin === CONSENT_ORIGIN && parsed.protocol === 'https:' && !parsed.username && !parsed.password
      ? { url: parsed.toString() } : unavailable;
  } catch {
    return unavailable;
  }
}

/** Outcome of the pre-start gate for one /investigate run. */
export type InvestigationGate = { ok: true; deepConsent: boolean }
  | { ok: false; state: Exclude<InvestigationPreflight, 'available'> | Exclude<CodexGrantState, 'granted'> }
  | { ok: false; state: 'deep_declined' };

/** Gate a start in order: runtime preflight, then the chatbox grant, then (deep only) explicit per-run
 * consent. Consent is asked only after both checks pass, so nobody consents to a run that cannot start.
 * `priorDeepConsent` is this same run's earlier consent (a retry of the same message/key). Returns null
 * when the chat moved on while a check was in flight (`isCurrent` false). Sends no start itself. */
export async function gateInvestigationStart(sessionId: string, tier: CodexTier, options: {
  priorDeepConsent: boolean; confirmDeep: () => boolean; isCurrent: () => boolean;
}): Promise<InvestigationGate | null> {
  const preflight = await investigationPreflight(sessionId);
  if (!options.isCurrent()) return null;
  if (preflight !== 'available') return { ok: false, state: preflight };
  const grant = await readCodexGrant();
  if (!options.isCurrent()) return null;
  if (grant !== 'granted') return { ok: false, state: grant };
  if (tier !== 'deep') return { ok: true, deepConsent: false };
  if (options.priorDeepConsent) return { ok: true, deepConsent: true };
  let confirmed = false;
  try { confirmed = options.confirmDeep() === true; } catch { confirmed = false; }
  return confirmed ? { ok: true, deepConsent: true } : { ok: false, state: 'deep_declined' };
}

/** Owned runs for this chat (newest first) to recover the same run after a reload.
 * Returns null when the list cannot be verified. */
export async function listCodexRuns(sessionId: string): Promise<CodexRunListEntry[] | null> {
  try {
    const headers = await authHeaders();
    if (!headers.Authorization) return null;
    const response = await fetch(chatPath(sessionId, '/codex-runs'), { headers, cache: 'no-store' });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    if (!body || typeof body !== 'object' || !Array.isArray((body as { runs?: unknown }).runs)) return null;
    const entries: CodexRunListEntry[] = [];
    for (const item of (body as { runs: unknown[] }).runs) {
      const receipt = decodeCodexReceipt(item);
      if (receipt) { entries.push({ kind: 'run', receipt }); continue; }
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const row = item as Record<string, unknown>;
        if ((row.state === 'unconfirmed' || row.state === 'denied' || row.state === 'abandoned') && typeof row.idempotency_key === 'string') {
          entries.push({ kind: row.state, idempotencyKey: row.idempotency_key });
          continue;
        }
      }
      return null; // One unverifiable entry makes the whole list untrustworthy.
    }
    return entries;
  } catch {
    return null;
  }
}
